import React, { useEffect } from 'react';
import { X, Eye, ChevronRight, FileText } from 'lucide-react';
import { suggestionToCard, getEvidenceTablesForModal } from '../../utils/optimizationTransformers';
import { truncateCampaignNameForDisplay } from '../../utils/campaignUtils';

const OptimizationsListModal = ({
  isOpen,
  onClose,
  optimizations = [],
  groups = null,
  getObjectiveName,
  onSelectOptimization,
  onOpenEvidence,
  objectiveName,
  variant = 'table',
}) => {
  const useGroups = Array.isArray(groups) && groups.length > 0 && typeof getObjectiveName === 'function';
  const totalCount = useGroups
    ? groups.reduce((n, g) => n + (g.suggestions?.length ?? 0), 0)
    : (optimizations?.length ?? 0);

  useEffect(() => {
    if (!isOpen && onClose) onClose();
  }, [isOpen, onClose]);

  if (!isOpen || totalCount === 0) return null;

  const handleSelect = (optimization, options) => {
    onSelectOptimization(optimization, options);
    onClose();
  };

  const getDaysUntilExpiration = (expiresAt) => {
    if (!expiresAt) return null;

    try {
      const expirationDate = new Date(expiresAt);
      const now = new Date();
      const diffTime = expirationDate - now;
      // #FIXME: Math.ceil can label same-day expirations as 1 day and past times as "Expira hoy".
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      console.error('Error calculando días hasta expiración:', error);
      return null;
    }
  };

  const renderCards = () => {
    if (useGroups) {
      return (
        <div className="space-y-4">
          {groups.map((group, groupIdx) => {
            const suggestions = group.suggestions ?? [];
            if (suggestions.length === 0) return null;
            const ruleName = group.rule?.name || group.rule?.description || 'Optimización';
            const ruleKey = group.rule?.id ?? groupIdx;
            const groupCards = suggestions.map((s) => suggestionToCard(s, getObjectiveName));
            const firstOpt = groupCards[0];
            const countLabel = suggestions.length > 1 ? ` (${suggestions.length} sugerencias)` : '';
            return (
              <div key={ruleKey} className="rounded-xl border border-white/20 bg-white/5 overflow-hidden">
                <div className="px-4 py-2 border-b border-white/20 bg-white/10 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-white min-w-0 truncate">{ruleName}</h3>
                  {group.primary_kpi && (
                    <span className="shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white/80">
                      {group.primary_kpi}
                    </span>
                  )}
                </div>
                <div className="flex items-start justify-between p-4 hover:bg-white/10 transition-colors">
                  <div
                    onClick={() => handleSelect(firstOpt, { allForGroup: groupCards })}
                    className="flex-1 cursor-pointer min-w-0"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        firstOpt.type === 'ESCALAR' ? 'bg-green-500/20 text-green-100 border border-green-400/30' :
                        firstOpt.type === 'OPTIMIZAR' ? 'bg-blue-500/20 text-blue-100 border border-blue-400/30' :
                        'bg-red-500/20 text-red-100 border border-red-400/30'
                      }`}>
                        {firstOpt.type}
                      </span>
                      <h3 className="text-sm font-semibold text-white">{firstOpt.action}{countLabel}</h3>
                    </div>
                    <div className="space-y-1 text-xs text-white/70">
                      <div>
                        <span className="font-semibold">Campaña:</span>{' '}
                        {suggestions.length > 1 ? `${suggestions.length} campañas` : firstOpt.campaign_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    {onOpenEvidence && getEvidenceTablesForModal(firstOpt.evidence).length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const tables = getEvidenceTablesForModal(firstOpt.evidence);
                          if (tables.length > 0) onOpenEvidence(tables);
                        }}
                        className="min-h-[44px] px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                        title="Ver evidencia"
                        aria-label="Ver evidencia"
                      >
                        <FileText size={14} />
                        Ver evidencia
                      </button>
                    )}
                    <div onClick={() => handleSelect(firstOpt, { allForGroup: groupCards })} className="cursor-pointer p-1" aria-hidden>
                      <ChevronRight className="w-5 h-5 text-white/50" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {optimizations.map((opt, idx) => (
          <div
            key={opt.id || idx}
            onClick={() => handleSelect(opt)}
            className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl p-4 hover:bg-white/20 hover:border-white/30 cursor-pointer transition-all duration-300 hover:scale-[1.01]"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    opt.type === 'ESCALAR' ? 'bg-green-500/20 text-green-100 border border-green-400/30' :
                    opt.type === 'OPTIMIZAR' ? 'bg-blue-500/20 text-blue-100 border border-blue-400/30' :
                    'bg-red-500/20 text-red-100 border border-red-400/30'
                  }`}>
                    {opt.type}
                  </span>
                  <h3 className="text-sm font-semibold text-white">{opt.action}</h3>
                </div>
                <div className="space-y-1 text-xs text-white/70">
                  <div>
                    <span className="font-semibold">Campaña:</span> {opt.campaign_name}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/50 flex-shrink-0 ml-4" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTable = () => {
    const renderRow = (opt, idx, rowClass = '') => {
      const objectiveLabel = opt.objective_name || objectiveName || 'Objetivo';
      const platformLabel = opt.platform || 'N/A';
      const daysUntilExpiration = getDaysUntilExpiration(opt.expires_at);
      return (
        <tr key={opt.id ?? idx} className={rowClass}>
          <td className="px-4 py-3 whitespace-nowrap">
            <div className="text-sm font-semibold text-white">{opt.action || '-'}</div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-center">
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${platformLabel === 'Google Ads' ? 'bg-blue-400' : 'bg-purple-400'}`} />
              <span className="text-sm text-white/80">{platformLabel}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap min-w-0">
            {(() => {
              const { display, title } = truncateCampaignNameForDisplay(opt.campaign_name);
              return (
                <div className="text-sm text-white/80 truncate" title={title || undefined}>
                  {display || '-'}
                </div>
              );
            })()}
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-100 border border-green-400/30">
              {objectiveLabel}
            </span>
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-center">
            {daysUntilExpiration === null ? (
              <div className="text-sm text-white/50">-</div>
            ) : daysUntilExpiration === 0 ? (
              <div className="text-sm text-red-300">Expira hoy</div>
            ) : (
              <div className="text-sm text-white/70">
                Expira en {daysUntilExpiration} {daysUntilExpiration === 1 ? 'día' : 'días'}
              </div>
            )}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-center">
            <div className="flex items-center justify-center gap-2">
              {onOpenEvidence && getEvidenceTablesForModal(opt.evidence).length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const tables = getEvidenceTablesForModal(opt.evidence);
                    if (tables.length > 0) onOpenEvidence(tables);
                  }}
                  className="min-h-[44px] px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  title="Ver evidencia"
                  aria-label="Ver evidencia"
                >
                  <FileText size={14} />
                  Ver evidencia
                </button>
              )}
              <button
                type="button"
                onClick={() => handleSelect(opt)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 backdrop-blur-md border border-blue-400/30 shadow-lg hover:border-blue-400/50 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center space-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                title="Ver detalles"
              >
                <Eye size={14} />
                <span>Ver detalles</span>
              </button>
            </div>
          </td>
        </tr>
      );
    };

    if (useGroups) {
      return (
        <div className="overflow-x-auto space-y-4">
          {groups.map((group, groupIdx) => {
            const suggestions = group.suggestions ?? [];
            if (suggestions.length === 0) return null;
            const ruleName = group.rule?.name || group.rule?.description || 'Optimización';
            const ruleKey = group.rule?.id ?? groupIdx;
            const groupCards = suggestions.map((s) => suggestionToCard(s, getObjectiveName));
            const firstOpt = groupCards[0];
            const countLabel = suggestions.length > 1 ? ` (${suggestions.length})` : '';
            return (
              <div key={ruleKey} className="rounded-xl border border-white/20 bg-white/5 overflow-hidden">
                <div className="px-4 py-2 border-b border-white/20 bg-white/10 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-white min-w-0 truncate">{ruleName}</div>
                  {group.primary_kpi && (
                    <span className="shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white/80">
                      {group.primary_kpi}
                    </span>
                  )}
                </div>
                <table className="w-full table-fixed">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '14%' }} />
                  </colgroup>
                  <tbody className="divide-y divide-white/10">
                    <tr className="bg-transparent hover:bg-white/5">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-semibold text-white truncate" title={firstOpt.action}>{firstOpt.action || '-'}{countLabel}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${firstOpt.platform === 'Google Ads' ? 'bg-blue-400' : 'bg-purple-400'}`} />
                          <span className="text-sm text-white/80">{firstOpt.platform || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap min-w-0">
                        {(() => {
                          const content =
                            suggestions.length > 1
                              ? { display: `${suggestions.length} campañas`, title: '' }
                              : truncateCampaignNameForDisplay(firstOpt.campaign_name);
                          const display = content.display || '-';
                          return (
                            <div className="text-sm text-white/80 truncate" title={content.title || undefined}>
                              {display}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-100 border border-green-400/30">
                          {firstOpt.objective_name || objectiveName || 'Objetivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const days = getDaysUntilExpiration(firstOpt.expires_at);
                          if (days === null) return <div className="text-sm text-white/50">-</div>;
                          if (days === 0) return <div className="text-sm text-red-300">Expira hoy</div>;
                          return (
                            <div className="text-sm text-white/70">
                              Expira en {days} {days === 1 ? 'día' : 'días'}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          {onOpenEvidence && getEvidenceTablesForModal(firstOpt.evidence).length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const tables = getEvidenceTablesForModal(firstOpt.evidence);
                                if (tables.length > 0) onOpenEvidence(tables);
                              }}
                              className="min-h-[44px] px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                              title="Ver evidencia"
                              aria-label="Ver evidencia"
                            >
                              <FileText size={14} />
                              Ver evidencia
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleSelect(firstOpt, { allForGroup: groupCards })}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 backdrop-blur-md border border-blue-400/30 shadow-lg hover:border-blue-400/50 hover:shadow-xl hover:shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center space-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                            title="Ver detalles"
                          >
                            <Eye size={14} />
                            <span>Ver detalles</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/30">
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Acción</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Plataforma</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Campaña</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Objetivo</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Expiración</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/20">
            {optimizations.map((opt, idx) => renderRow(opt, idx, idx % 2 === 0 ? 'bg-white/5' : 'bg-white/10'))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 modal-safe-area z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} role="presentation">
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/30 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative z-10 p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white mb-2">
                Optimizaciones Disponibles
              </h2>
              {objectiveName && (
                <div className="mb-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-100 border border-green-400/30">
                    {objectiveName}
                  </span>
                </div>
              )}
              <p className="text-sm text-white/70">
                Selecciona una optimización para ver los detalles
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Lista de optimizaciones */}
          {variant === 'cards' ? renderCards() : renderTable()}
        </div>
      </div>
    </div>
  );
};

export default OptimizationsListModal;
